import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

import { PuSubscribable } from '@app/utils/pu-subscribe';

@Injectable({
  providedIn: 'root',
})
export class PendingService extends PuSubscribable {
  private router: Router = inject(Router);

  private _loading$ = new BehaviorSubject<boolean>(false);
  private _loadingMap = new Map<string, boolean>();
  private _loadingMapSubject = new BehaviorSubject<Map<string, boolean>>(this._loadingMap);

  pending$: Observable<boolean> = this._loading$.pipe(debounceTime(300), distinctUntilChanged());

  constructor() {
    super();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.clearAllPending();
      })
      .untilDestroyed(this);
  }

  setLoading(loading: boolean, url: string): void {
    if (!url) throw new Error('Url must be provided here');
    if (url.endsWith('/api/notification')) return;

    if (loading) {
      this._loadingMap.set(url, loading);
      this._loading$.next(true);
      this._loadingMapSubject.next(this._loadingMap);
    } else if (!loading && this._loadingMap.has(url)) {
      this._loadingMap.delete(url);
      this.updateLoadingState();
    }
  }

  clearPending(url: string): void {
    if (this._loadingMap.has(url)) {
      this._loadingMap.delete(url);
      this.updateLoadingState();
    }
  }

  private updateLoadingState(): void {
    this._loading$.next(this._loadingMap.size > 0);
    this._loadingMapSubject.next(this._loadingMap);
  }

  private clearAllPending(): void {
    this._loadingMap.clear();
    this._loading$.next(false);
    this._loadingMapSubject.next(this._loadingMap);
  }

  isPendingUrls(urls: string[]): boolean {
    return urls.some(url => this._loadingMap.has(url));
  }

  isPendingExcludingUrls(excludedUrls: string[]): boolean {
    return Array.from(this._loadingMap.keys()).some(url => !excludedUrls.includes(url));
  }

  isPendingExcludingUrls$(excludedUrls: string[]) {
    return this._loadingMapSubject
      .asObservable()
      .pipe(map(loadingMap => Array.from(loadingMap.keys()).some(url => !excludedUrls.includes(url))));
  }
}
